import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";

import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

import DashboardLayout from "../layout/DashboardLayout";

import Banner1 from "../assets/Banner_1.jpeg";
import Banner2 from "../assets/Banner_2.jpeg";

const FACILITIES = [
  { key: "audio_mic", title: "Audio / Microphone Setup", desc: "Professional sound system for presentations" },
  { key: "projector", title: "Projector / Screen", desc: "Display for slides and visual presentations" },
  { key: "tea_coffee", title: "Tea, Coffee, Refreshments", desc: "Basic refreshments service for attendees" },
  { key: "catering", title: "Catering Coordination", desc: "Help arranging meals or advanced catering" },
  { key: "support_staff", title: "Event Support Staff", desc: "Dedicated staff to assist during your event" }
];

function toDateInputValue(d = new Date()) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function BookSpace() {
  // About event
  const [eventTitle, setEventTitle] = useState("");
  const [eventType, setEventType] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [attendees, setAttendees] = useState("");

  // Date & Time
  const [preferredDate, setPreferredDate] = useState(toDateInputValue(new Date()));
  const [alternateDate, setAlternateDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  // Facilities
  const [facilities, setFacilities] = useState(() => {
    const init = {};
    FACILITIES.forEach((f) => (init[f.key] = false));
    return init;
  });

  // Host details
  const [hostName, setHostName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  // Notes
  const [notes, setNotes] = useState("");

  const banners = useMemo(
    () => [
      { id: "b1", image: Banner1 },
      { id: "b2", image: Banner2 }
    ],
    []
  );

  const selectedFacilities = useMemo(
    () => Object.entries(facilities).filter(([, v]) => v).map(([k]) => k),
    [facilities]
  );

  const toggleFacility = (key) => {
    setFacilities((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = () => {
    if (!eventTitle.trim()) return alert("Please enter event title.");
    if (!eventType) return alert("Please select event type.");
    if (!preferredDate) return alert("Please select preferred date.");

    const payload = {
      eventTitle,
      eventType,
      shortDescription,
      attendees: attendees ? Number(attendees) : null,
      preferredDate,
      alternateDate: alternateDate || null,
      startTime: startTime || null,
      endTime: endTime || null,
      facilities: selectedFacilities,
      host: {
        hostName,
        orgName,
        contactEmail,
        contactPhone
      },
      notes: notes || null
    };

    console.log("Book space request payload:", payload);
    alert("Submit Booking Request (connect backend next).");
  };

  return (
    <DashboardLayout title="Dashboard">
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" }, gap: 3 }}>
        {/* LEFT */}
        <Box sx={{ minWidth: 0 }}>
          {/* Top banner strip */}
          <Card sx={{ bgcolor: "action.selected" }}>
            <CardContent sx={{ py: 2.25 }}>
              <Typography sx={{ fontWeight: 900 }}>
                Host your event at NeherBaari
              </Typography>
              <Typography sx={{ mt: 0.4, fontSize: 13, color: "text.secondary" }}>
                A calm, curated space for professional gatherings, workshops, and conversations.
              </Typography>
            </CardContent>
          </Card>

          {/* About Your Event */}
          <Box sx={{ mt: 3 }}>
            <Box className="sectionHeader">
              <Typography className="sectionHeaderTitle">About Your Event</Typography>
              <Box className="sectionHeaderLine" />
            </Box>

            <Card>
              <CardContent sx={{ py: 2.2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 2 }}>
                  <FormControl>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Event Name / Title
                    </Typography>
                    <TextField
                      value={eventTitle}
                      onChange={(e) => setEventTitle(e.target.value)}
                      placeholder="Enter event name"
                    />
                  </FormControl>

                  <FormControl>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Event Type
                    </Typography>
                    <TextField
                      select
                      value={eventType}
                      onChange={(e) => setEventType(e.target.value)}
                      placeholder="Select event type"
                    >
                      <MenuItem value="workshop">Workshop</MenuItem>
                      <MenuItem value="talk">Talk / Session</MenuItem>
                      <MenuItem value="meeting">Team Meeting</MenuItem>
                      <MenuItem value="community">Community Event</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </TextField>
                  </FormControl>

                  <FormControl sx={{ gridColumn: { xs: "auto", md: "1 / -1" } }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Short Event Description
                    </Typography>
                    <TextField
                      value={shortDescription}
                      onChange={(e) => setShortDescription(e.target.value)}
                      placeholder="Brief description of your event"
                      multiline
                      minRows={3}
                    />
                  </FormControl>

                  <FormControl sx={{ maxWidth: 260 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Estimated Number of Attendees
                    </Typography>
                    <TextField
                      value={attendees}
                      onChange={(e) => setAttendees(e.target.value)}
                      inputMode="numeric"
                      placeholder="0"
                    />
                  </FormControl>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Date & Time */}
          <Box sx={{ mt: 3 }}>
            <Box className="sectionHeader">
              <Typography className="sectionHeaderTitle">Date & Time</Typography>
              <Box className="sectionHeaderLine" />
            </Box>

            <Card>
              <CardContent sx={{ py: 2.2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
                  <FormControl>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Preferred Event Date
                    </Typography>
                    <TextField
                      type="date"
                      value={preferredDate}
                      onChange={(e) => setPreferredDate(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </FormControl>

                  <FormControl>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Start Time
                    </Typography>
                    <TextField
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccessTimeOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </FormControl>

                  <FormControl>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      End Time
                    </Typography>
                    <TextField
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccessTimeOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </FormControl>

                  <FormControl sx={{ gridColumn: { xs: "auto", md: "1 / span 1" } }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Alternate Date (Optional)
                    </Typography>
                    <TextField
                      type="date"
                      value={alternateDate}
                      onChange={(e) => setAlternateDate(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </FormControl>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Facilities & Add-ons */}
          <Box sx={{ mt: 3 }}>
            <Box className="sectionHeader">
              <Typography className="sectionHeaderTitle">Facilities & Add-ons (Optional)</Typography>
              <Box className="sectionHeaderLine" />
            </Box>

            <Card>
              <CardContent sx={{ py: 2.2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.2 }}>
                  {FACILITIES.map((f) => (
                    <FormControlLabel
                      key={f.key}
                      control={<Checkbox checked={Boolean(facilities[f.key])} onChange={() => toggleFacility(f.key)} />}
                      label={
                        <Box>
                          <Typography sx={{ fontWeight: 900, fontSize: 13 }}>
                            {f.title}
                          </Typography>
                          <Typography sx={{ fontSize: 12.2, color: "text.secondary" }}>
                            {f.desc}
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start", m: 0 }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Host Details */}
          <Box sx={{ mt: 3 }}>
            <Box className="sectionHeader">
              <Typography className="sectionHeaderTitle">Host Details</Typography>
              <Box className="sectionHeaderLine" />
            </Box>

            <Card>
              <CardContent sx={{ py: 2.2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                  <FormControl>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Host Name
                    </Typography>
                    <TextField value={hostName} onChange={(e) => setHostName(e.target.value)} placeholder="Your full name" />
                  </FormControl>

                  <FormControl>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Organization Name
                    </Typography>
                    <TextField value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Organization name (if applicable)" />
                  </FormControl>

                  <FormControl>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Contact Email
                    </Typography>
                    <TextField value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="you@example.com" />
                  </FormControl>

                  <FormControl>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Contact Phone Number
                    </Typography>
                    <TextField value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="+91 00000 00000" />
                  </FormControl>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Additional Notes */}
          <Box sx={{ mt: 3 }}>
            <Box className="sectionHeader">
              <Typography className="sectionHeaderTitle">Additional Notes</Typography>
              <Box className="sectionHeaderLine" />
            </Box>

            <Card>
              <CardContent sx={{ py: 2.2 }}>
                <FormControl fullWidth>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                    Special Requests
                  </Typography>
                  <TextField
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Share any specific requirements or context we should be aware of."
                    multiline
                    minRows={4}
                  />
                </FormControl>
              </CardContent>
            </Card>
          </Box>

          {/* Request Summary */}
          <Box sx={{ mt: 3 }}>
            <Card sx={{ bgcolor: "action.selected" }}>
              <CardContent sx={{ py: 2.2 }}>
                <Typography sx={{ fontWeight: 900, fontSize: 13.5 }}>
                  Request Summary
                </Typography>

                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                      <Typography sx={{ fontSize: 12.8, color: "text.secondary", fontWeight: 700 }}>
                        Event Type
                      </Typography>
                      <Typography sx={{ fontSize: 12.8, fontWeight: 900 }}>
                        {eventType || "Not selected"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                      <Typography sx={{ fontSize: 12.8, color: "text.secondary", fontWeight: 700 }}>
                        Preferred Date
                      </Typography>
                      <Typography sx={{ fontSize: 12.8, fontWeight: 900 }}>
                        {preferredDate || "Not selected"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                      <Typography sx={{ fontSize: 12.8, color: "text.secondary", fontWeight: 700 }}>
                        Estimated Attendees
                      </Typography>
                      <Typography sx={{ fontSize: 12.8, fontWeight: 900 }}>
                        {attendees || "—"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                      <Typography sx={{ fontSize: 12.8, color: "text.secondary", fontWeight: 700 }}>
                        Add-ons
                      </Typography>
                      <Typography sx={{ fontSize: 12.8, fontWeight: 900 }}>
                        {selectedFacilities.length ? `${selectedFacilities.length} selected` : "None selected"}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1.6 }} />

                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button variant="contained" onClick={handleSubmit} sx={{ px: 5, py: 1.2 }}>
                      Submit Booking Request
                    </Button>
                  </Box>

                  <Typography sx={{ mt: 1.1, fontSize: 12, color: "text.secondary", textAlign: "center" }}>
                    Our team will review your request and get back to you shortly.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* RIGHT banners */}
        <Box sx={{ display: { xs: "none", lg: "flex" }, flexDirection: "column", gap: 2 }}>
          {banners.map((b) => (
            <Card key={b.id} sx={{ overflow: "hidden", bgcolor: "background.paper" }}>
              <Box sx={{ p: 1.5, bgcolor: "action.hover" }}>
                <Box
                  sx={{
                    height: 320,
                    borderRadius: 0,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                    backgroundImage: `url(${b.image})`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "contain"
                  }}
                />
              </Box>

              <Box sx={{ p: 1.5, pt: 1.2 }}>
                <Button fullWidth variant="outlined" sx={{ py: 1 }}>
                  Learn More
                </Button>
              </Box>
            </Card>
          ))}
        </Box>
      </Box>
    </DashboardLayout>
  );
}
